import { LOGO_BASE64 } from '../assets/logoBased64';
import { getLabFullName } from './labMappings.config';
import { Method } from './s3publicfiles.config';

// Helper function to convert method to display name
const getMethodDisplayName = (method: Method): string => {
  switch (method) {
    case Method.SIRA:
      return 'Stable Isotope Ratio Analysis (SIRA)';
    case Method.XRF:
      return 'X-Ray Fluorescence (XRF)';
    case Method.ICPMS:
      return 'Inductively Coupled Plasma Mass Spectrometry (ICPMS)';
    case Method.SIRAXRF:
      return 'Stable Isotope Ratio Analysis (SIRA) & X-Ray Fluorescence (XRF)';
    default:
      return method;
  }
};

const HEADER_SIZE = 16;
const TEXT_SIZE = 12;

const LINK =
  'https://learn.worldforestid.org/wp-content/uploads/WFID-Evaluation-Report-Annex.pdf';

const defaultLogoHeader = { height: 36, width: 70, image: LOGO_BASE64 };

export const docDefinition = async () => {
  return {
    pageMargins: [40, 100, 40, 60], // [left, top, right, bottom] - increased top margin for header
    header: () => {
      return { 
        ...defaultLogoHeader,
        margin: [30, 30, 0, 0] // Add proper margins for header positioning
      };
    },
    footer: (currentPage: number) => {
      if (currentPage === 1) {
        return {
          margin: [30, 0, 10, 0],
          columns: [
            {
              width: '80%',
              stack: [
                {
                  margin: [0, -20, 0, 0],
                  canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 1 }],
                },
                {
                  margin: [0, 5, 10, 0],
                  text: '¹ The genus and species should be confirmed by either wood anatomical, DNA or chemical analysis to increase certainty of results.',
                  fontSize: 12,
                },
              ],
            },
            {
              text: currentPage.toString(),
              alignment: 'right',
              margin: [0, 20, 10, 0],
            },
          ],
        };
      }
      return {
        text: currentPage.toString(),
        margin: [0, 20, 10, 0],
        alignment: 'right',
        fontSize: 12,
      };
    },
    styles: {
      header: {
        fontSize: 18,
        margin: [0, 10, 30, 30],
        fontWeight: 400,
      },
      subheader: {
        fontSize: 17,
        margin: [0, 0, 30, 30],
        fontWeight: 400,
      },
      tableHeader: {
        bold: true,
        alignment: 'left',
      },
    },
    defaultStyle: {
      lineHeight: 1.5,
    },
  };
};
export const generatePDFPage1 = (params: any) => {
  const {
    reportNumber,
    totalPixels,
    claimedOrigin,
    selectedGenus,
    commonName,
    selectedMethod,
    firstColumn,
    secondColumn,
    lab,
    measurementLabId,
    speciesVerifiedBy,
  } = params;
  return [
    { text: ['WFID EVALUATION REPORT: ', { text: reportNumber }], style: 'header' },
    { text: 'Verification', style: 'header', margin: [0, -10, 30, 30]},

    {
      columns: [
        {
          margin: [0, 0, 0, 10],
          fontSize: HEADER_SIZE,
          text: 'Sample Details',
          bold: true,
        },
        {
          text: 'Testing Procedure',
          margin: [0, 0, 0, 10],
          fontSize: HEADER_SIZE,
          bold: true,
        },
      ],
    },
    {
      columns: [
        {
          text: 'Claimed Species¹',
          fontSize: 12,
          bold: true,
        },
        { text: 'Origin Technique Used', fontSize: TEXT_SIZE, bold: true },
      ],
    },
    {
      columns: [
        {
          text: [
            { text: `${selectedGenus} `, italics: true },
            'spp. ',
            { text: ` (${commonName})` }
          ],
          fontSize: TEXT_SIZE,
          margin: [0, 5, 0, 0],
        },
        {
          text: getMethodDisplayName(selectedMethod),
          fontSize: TEXT_SIZE,
          margin: [0, 5, 0, 0],
        },
      ],
    },

    // break

    {
      margin: [0, 20, 0, 0],
      columns: [
        {
          text: 'Species verified by',
          fontSize: TEXT_SIZE,
          bold: true,
        },
        {
          text: 'Measurement laboratory',
          fontSize: 12,
          bold: true,
        },
      ],
    },
    {
      columns: [
        {
          text: speciesVerifiedBy,
          fontSize: TEXT_SIZE,
          margin: [0, 5, 0, 0],
        },
        {
          text: getLabFullName(lab),
          fontSize: TEXT_SIZE,
          margin: [0, 5, 0, 0],
        },
      ],
    },

    // break

    {
      margin: [0, 20, 0, 0],
      columns: [
        {
          text: 'Claimed Origin',
          fontSize: TEXT_SIZE,
          bold: true,
        },
        {
          text: 'Measurement Laboratory ID',
          fontSize: TEXT_SIZE,
          bold: true,
        },
      ],
    },
    {
      columns: [
        {
          text: claimedOrigin ?? 'None',
          fontSize: TEXT_SIZE,
          margin: [0, 5, 0, 0],
        },
        {
          text: measurementLabId ?? 'N/A',
          fontSize: TEXT_SIZE,
          margin: [0, 5, 0, 0],
        },
      ],
    },
    {
      margin: [0, 20, 0, 0],
      columns: [
        {
          text: 'Platform Evaluation Date',
          fontSize: TEXT_SIZE,
          bold: true,
        },
      ],
    },
    {
      columns: [
        {
          text: new Date().toLocaleDateString('en-GB', { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          }),
          fontSize: TEXT_SIZE,
          margin: [0, 5, 0, 0],
        },
      ],
    },

    { text: 'Results of Analysis', bold: true, margin: [0, 40, 0, 0], fontSize: HEADER_SIZE },
    {
      text: [
        'Stable isotope ratio values for the test sample differ to a statistically significant degree for ',
        { text: `${totalPixels}%` },
        ' known and inferred values in the claimed location. See map in the Mapped Verification Results section.',
      ],
      margin: [0, 10, 0, 0],
      fontSize: TEXT_SIZE,
    },

    // break
    { text: 'Test Sample Measurements (uploaded)', style: 'subheader', pageBreak: 'before' },

    // break
    {
      margin: [0, 10, 0, 0], 
      table: {
        widths: ['30%', '70%'], 
        body: [
          // Header row
          [
            { text: 'Measurement:', fontSize: TEXT_SIZE, bold: true, alignment: 'left' },
            { text: 'Value', fontSize: TEXT_SIZE, bold: true, alignment: 'left' }
          ],
          // Data rows - combine firstColumn and secondColumn (leaving data split incase return to columns later)
          ...firstColumn.map((item: string) => {
            const parts = item.split(':');
            return [
              { text: (parts[0] || '') + ':', fontSize: TEXT_SIZE },
              { text: parts[1]?.trim() || '', fontSize: TEXT_SIZE }
            ];
          }),
          ...secondColumn.map((item: string) => {
            const parts = item.split(':');
            return [
              { text: (parts[0] || '') + ':', fontSize: TEXT_SIZE },
              { text: parts[1]?.trim() || '', fontSize: TEXT_SIZE }
            ];
          })
        ]
      },
      layout: {
        hLineWidth: function() { return 0; }, // Remove horizontal lines
        vLineWidth: function() { return 0; }, // Remove vertical lines
        hLineColor: function() { return '#fff'; }, // Transparent lines
        vLineColor: function() { return '#fff'; }, // Transparent lines
      },
      pageBreak: 'after'
    },
  ];
};

// INFO: Second page of the PDF
export const generatePDFPage2 = (params: any) => {
  const { selectedGenus, tableContent } = params;

  return [
    { text: 'Methodology', bold: true, style: 'subheader' },
    {
      margin: [0, 0, 0, 20],
      text: 'The World Forest ID sub-national evaluation process compares the isotopic signature obtained from test samples against a spatial model. This model is trained using stable isotope ratio reference values from ground-truthed samples taken from forests where the genus or species in question grows. The model infers the stable isotope ratio values for areas where ground-truthed samples are unavailable to allow continuous landscape mapping and sub-national verification.',
    },
    {
      margin: [0, 0, 0, 20],
      text: 'This peer-reviewed method effectively accounts for natural variability in isotopic signatures due to environmental factors across different geographic regions, allowing for detailed evaluation of location of national and sub-national harvest claims .',
    },
    {
      margin: [0, 0, 0, 20],
      text: 'It is possible to perform A verification test, which is a triangulation of reference chemical measurements, geolocation claim and chemical measurements for the test sample. Results for this function have relatively low uncertainty.',
    },
    {
      margin: [0, 0, 0, 20],
      text: [
        'Please download the full method annex for this report ',
        { text: 'here.', link: LINK, decoration: 'underline' },
      ],
    },
    {
      margin: [0, 0, 0, 20],
      text: 'World Forest ID ground-truthed reference samples',
      style: 'subheader',
      pageBreak: 'before',
    },

    /// table
    {
      margin: [0, 0, 0, 20],
      table: {
        widths: [120, 200],
        body: [
          [
            { text: 'Country', style: 'tableHeader', fontSize: TEXT_SIZE },
            { text: [
              { text: `${selectedGenus} `, italics: true },
              'spp. Samples'
            ], style: 'tableHeader', fontSize: TEXT_SIZE },
          ],
          ...tableContent,
        ],
      },
      layout: {
        hLineWidth: function() { return 0.5; },
        vLineWidth: function() { return 0.5; },
        hLineColor: function() { return '#fff'; },
        vLineColor: function() { return '#fff'; },
      },
    },
  ];
};

// INFO: Third page of the PDF
export const generatePDFPage3 = (textContent?: string) => {
  return [
    { text: 'Third-Party Data Sources', bold: true, style: 'subheader', pageBreak: 'before' },
    {
      margin: [0, 0, 0, 20],
      text: textContent,
      pageBreak: 'after',
    },
  ];
};

// INFO: Third page of the PDF
export const generatePDFPage4 = (params: { mapImage: string | undefined }) => {
  const { mapImage } = params;

  return [
    {
      text: 'Mapped Verification Results',
      bold: true,
      style: 'subheader',

    },
    {
      margin: [0, 0, 0, 10],
      fontSize: TEXT_SIZE,
      text: 'Map shows the sub-national spatial model evaluation result, comparing the isotopic signature of the test sample with isotopic signature for harvest locations where World Forest ID have ground-truthed and inferred data points for all other harvest locations in the native range of the claimed genus/species in the claimed origin/location of harvest. ',
    },
    {
      margin: [0, 0, 0, 20],
      fontSize: TEXT_SIZE,
      text: 'Red areas in Map indicate that the isotopic signature for the test sample differs to a statistically significant degree from all ground-truthed and inferred data points. ',
    },

    {
      margin: [0, 0, 0, 30],
      width: 490,
      image: mapImage,
      fit: [490, 400], // Maintain aspect ratio with max height of 400
    },

    { text: 'Green areas indicate locations which could be the harvest location.', margin: [0, 0, 0, 10] },
    {
      pageBreak: 'after',
      text: 'Red areas indicate locations which cannot be the harvest location.',
    },
  ];
};

// INFO: Third page of the PDF
export const generatePDFPage5 = () => {
  return [
    { text: 'What is World Forest ID?', bold: true, style: 'subheader' },
    {
      margin: [0, 0, 0, 30],
      fontSize: TEXT_SIZE,
      text: 'World Forest ID is a US-registered non-profit established in 2021 with support from the Royal Botanic Gardens, Kew, the US Forest Service, the Forest Stewardship Council and the World Resources Institute. Our mission is to create unique geo-located chemical, environmental, and geospatial data that can be leveraged by machine learning models to verify timber and forest risk commodity harvest location. World Forest ID is supported by a consortium of scientific institutions which provide oversight of the integrity of our scientific strategy. The evaluation in this report are based on stable isotope ratio measurements performed in an external laboratory.',
    },
    { text: 'Liability', bold: true, style: 'subheader', pageBreak: 'before' },
    {
      margin: [0, 0, 0, 10],
      fontSize: TEXT_SIZE,
      text: 'This report was automatically generated using the World Forest ID Platform and subject to the following liability clauses in line with the World Forest ID Platform Terms of Service.',
    },
    {
      margin: [0, 0, 0, 10],
      fontSize: TEXT_SIZE,
      text: 'a. Under no circumstances will World Forest ID (“WFID”, “We”, Our”, “Us”) be liable for any special, indirect, incidental, consequential or exemplary damages or for any loss of revenue, profits or anticipated savings, data, or use or cost of cover arising from or in connection with the World Forest ID Platform (Services) or Data (including Your use of, or inability to use the Services or Data), even if We were aware of the possibility of such loss or damages.',
    },
    {
      margin: [0, 0, 0, 10],
      fontSize: TEXT_SIZE,
      text: 'b. When You request to open an account on the World Forest ID Organization page on the ESRI platform, the following conditions must be met: (1) You are applying on behalf of an organization or entity that works with and is actively involved in commodity supply chains and/or their financing, enforcement, or representing civil society and/or media outlets; (2) You are authorised to act on behalf of such organisation or entity and (3) the application is sent from a valid email from the organisation or entity. World Forest ID may request additional information in order to confirm these conditions are met.',
    },
    {
      margin: [0, 0, 0, 10],
      fontSize: TEXT_SIZE,
      text: 'c. You must protect the security of Your account. You are responsible for Your use of the Services and all activity that occurs under Your account. You must keep Your password and any other authentication information confidential and must not disclose it to anyone else. If You know or suspect that anyone other than You knows Your password or any other authentication information, You must promptly notify Us. We are not responsible for any losses or liabilities arising out of or in connection with any use of the Services by unauthorised individuals.',
    },
    {
      margin: [0, 0, 0, 10],
      fontSize: TEXT_SIZE,
      text: 'd. We may cancel or suspend Your access to the Services at any time, without notice where, in Our reasonable opinion, You have failed to comply with any provision of these Terms. Upon cancellation or suspension, Your right to use the Services will end immediately. You will have the right to access any Content that belongs to You which is stored on the Services for a period of fifteen days following cancellation or suspension; thereafter You may not have access to Your Content stored on the Services.',
    },
  ];
};

// INFO: page of the PDF
export const generatePDFPage6 = () => {
  return [
    { text: 'Glossary of Terms', bold: true, style: 'subheader', pageBreak: 'before' },
    // {
    //   margin: [30, 0, 0, 10],
    //   fontSize: TEXT_SIZE,
    //   text: [
    //     { text: 'Determination: ', fontWeight: 600, bold: true },
    //     'testing, using Spatial Models, to identify the most likely harvest location based on the comparison between the Isotopic Signature derived from the Test Sample and the Isotopic Signature of the Ground-Truthed and Inferred Data Points across the reference data range. This test does not take into account the claimed harvest location.',
    //   ],
    // },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'Ground-Truthed Data Points: ', fontWeight: 600, bold: true },
        'Measurements or observations collected directly from specific locations or samples that are used to verify and calibrate remote sensing data, models, or predictions in scientific research.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'Inferred Data Points: ', fontWeight: 600, bold: true },
        'Locations with chemical data collected from the spatial model as no ground-truthed data points are available on those locations.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'Isotopic Signature: ', fontWeight: 600, bold: true },
        'The unique pattern of stable isotope ratios (such as δ2H, δ18O, δ13C, δ34S) in a material that can be linked to a specific geographical region and environmental conditions.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'Reference Sample: ', fontWeight: 600, bold: true },
        'samples taken from forests in controlled conditions, used to create known values for wood and from specific locations, in order to facilitate comparison.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'Spatial Models: ', fontWeight: 600, bold: true },
        'Mathematical or computational simulations used to represent and analyze the spatial distributions and relationships of geographic data.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'Test Sample: ', fontWeight: 600, bold: true },
        'physical forest products, taken from supply chains in order to use science to identify the location of harvest or evaluate the credibility of a location of harvest / origin claim for the wood from which they are made.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'Verification: ', fontWeight: 600, bold: true },
        'evaluating, using Spatial Models, whether the harvest location claim is plausible based on the comparison between the Isotopic Signature  derived from the Test Sample and the Isotopic Signature of the Ground-Truthed and Inferred Data Points in the claimed harvest location.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'δ13C (Carbon-13): ', fontWeight: 600, bold: true },
        'A stable isotope ratio of carbon; the δ13C ratio in plant material can indicate aspects of the plant’s growing conditions and photosynthetic pathways. ',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'δ2H (Deuterium): ', fontWeight: 600, bold: true },
        'A stable isotope ratio of hydrogen; variations in the δ2H values in plant material is used to infer information about the water sources influencing the growth environment of plants.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'δ2H nitrated (Deuterium): ', fontWeight: 600, bold: true },
        'A stable isotope ratio of hydrogen on nitrated wood; variations in the δ2H values in plant material is used to infer information about the water sources influencing the growth environment of plants.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'δ15N (Nitrogen-15): ', fontWeight: 600, bold: true },
        'A stable isotope ratio of nitrogen; the δ15N ratio in plant material.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'δ18O (Oxygen-18): ', fontWeight: 600, bold: true },
        'A stable isotope ratio of oxygen; variations in the δ18O values in plant material can reflect environmental conditions and are used to trace the geographic origin of the sample.',
      ],
    },
    {
      margin: [20, 0, 0, 10],
      fontSize: TEXT_SIZE,
      pageBreak: 'avoid',
      text: [
        { text: 'δ34S (Sulfur-34): ', fontWeight: 600, bold: true },
        'A stable isotope ratio  of sulfur; used to reflect the sulfur isotopic composition in materials to determine geographic and environmental backstories.',
      ],
    },
  ];
};
